import { type SxProps, type Theme } from "@mui/material";

import { Icon, type IconType } from "../../redesign/components/Icon";
import { Pill } from "../../redesign/components/Pill";
import { Text } from "../../redesign/components/Text";
import { type CredentialAccountStatus } from "../utils/credentialAccountStatusHelpers";

interface CredentialAccountStatusChipProps {
  status: CredentialAccountStatus;
  scheduledTimeText?: string;
}

interface StatusConfig {
  label: string;
  iconType?: IconType;
  sx: SxProps<Theme>;
  color: string;
}

function getStatusConfig(
  status: CredentialAccountStatus,
  scheduledTimeText?: string
): StatusConfig {
  // Exhaustive check so default case is not needed
  // eslint-disable-next-line default-case
  switch (status) {
    case "active": {
      return {
        label: "Active",
        iconType: "check",
        sx: (theme: Theme) => ({
          backgroundColor: theme.palette.statusChip?.success.background,
          borderColor: "transparent",
        }),
        color: "statusChip.success.text",
      };
    }
    case "scheduled": {
      return {
        label: scheduledTimeText ?? "Scheduled",
        iconType: "calendar",
        sx: (theme: Theme) => ({
          backgroundColor: theme.palette.statusChip?.info.background,
          borderColor: "transparent",
        }),
        color: "statusChip.info.text",
      };
    }
    case "not_scheduled": {
      return {
        label: "Not scheduled",
        sx: (theme: Theme) => ({
          backgroundColor: theme.palette.statusChip?.muted.background,
          borderColor: "transparent",
        }),
        color: "statusChip.muted.text",
      };
    }
    case "error": {
      return {
        label: "Error",
        iconType: "warning",
        sx: (theme: Theme) => ({
          backgroundColor: theme.palette.statusChip?.error.background,
          borderColor: "transparent",
        }),
        color: "statusChip.error.text",
      };
    }
    case "creating": {
      return {
        label: "Creating...",
        iconType: "clock-back",
        sx: (theme: Theme) => ({
          backgroundColor: theme.palette.statusChip?.pending.background,
          borderColor: "transparent",
        }),
        color: "statusChip.pending.text",
      };
    }
    case "resetting": {
      return {
        label: "Resetting...",
        iconType: "clock-back",
        sx: (theme: Theme) => ({
          backgroundColor: theme.palette.statusChip?.pending.background,
          borderColor: "transparent",
        }),
        color: "statusChip.pending.text",
      };
    }
    case "expired": {
      return {
        label: "Expired",
        iconType: "warning",
        sx: (theme: Theme) => ({
          backgroundColor: theme.palette.statusChip?.warning.background,
          borderColor: "transparent",
        }),
        color: "statusChip.warning.text",
      };
    }
  }
}

export function CredentialAccountStatusChip(props: CredentialAccountStatusChipProps) {
  const { status, scheduledTimeText } = props;
  const config = getStatusConfig(status, scheduledTimeText);

  return (
    <Pill size="small" sx={config.sx}>
      {config.iconType && (
        <Icon
          size="small"
          sx={{ color: config.color, marginRight: "5px" }}
          type={config.iconType}
        />
      )}
      <Text semibold sx={{ color: config.color }} variant="body2">
        {config.label}
      </Text>
    </Pill>
  );
}
